import React, { useEffect, useState, useContext } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Card, Button, Spinner, Alert, Container, Modal, Row, Col, Badge } from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup, Form } from "reactstrap";
import { useNavigate } from "react-router-dom";

import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import { getExtensionsSummary, toggleExtensions } from "./extensionService";
import HusariaHeader from "../../components/HusariaHeader";

const REG_STATUS = {
  REG: { label: "Reg", variant: "success" },
  UNREG: { label: "Unreg", variant: "danger" },
  UNKNOWN: { label: "Unknown", variant: "secondary" },
};

const ExtensionsTable = ({
    title = "Extensions",
    parentName = "HUCS",
}) => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [deleteModal, setDeleteModal] = useState(false);
    const [toggleModal, setToggleModal] = useState(false);
    const [extensionsSummary, setExtensionsSummary] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [deleteVoicemail, setDeleteVoicemail] = useState(true);
    const [canCreate, setCanCreate] = useState(true);
    const navigate = useNavigate();

    const userData = React.useMemo(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    }, []);

    const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
    const canDelete = hasPermission(userData, PERMISSIONS.DELETE);

    const pagination = paginationFactory({
        page: 1,
        alwaysShowAllBtns: true,
        showTotal: true,
        withFirstAndLast: false,
        sizePerPageRenderer: ({ onSizePerPageChange }) => (
            <div className="dataTables_length">
                <label>
                    Show{" "}
                    <select
                        className="form-control form-control-sm"
                        onChange={(e) => onSizePerPageChange(e.target.value)}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>{" "}
                    entries.
                </label>
            </div>
        ),
    });

    const selectRow = {
        mode: "checkbox",
        clickToSelect: false,
        selected: selectedRows,
        onSelect: (row, isSelect) => {
            setSelectedRows(prev =>
                isSelect
                    ? [...prev, row.extension_uuid]
                    : prev.filter(id => id !== row.extension_uuid)
            );
        },
        onSelectAll: (isSelect, rows) => {
            if (isSelect) {
                setSelectedRows(rows.map(r => r.extension_uuid));
            } else {
                setSelectedRows([]);
            }
        },
        classes: "checkbox-column",
        headerClasses: "checkbox-column-header",
    };

    const toBoolean = (val) => {
        if (typeof val === "boolean") return val;
        if (typeof val === "string") return val.toLowerCase() === "true";
        if (typeof val === "number") return val === 1;
        return false;
    };

    const displayValue = (val) =>
        val === null || val === undefined || val === "" ? "-" : val;

    const info_style = {
        backgroundColor: isDark ? "#1f2937" : "#fff",
        borderRadius: "1rem",
        overflow: "hidden",
    }

    /* ------------------ TABLE COLUMNS ------------------ */

    const columns = [
        {
            
            dataField: "reg_status",
            text: "Status",
            sort: false,
            formatter: (cell) => {
                const value = displayValue(cell)
                const config = REG_STATUS[value] ?? REG_STATUS.UNKNOWN
                return (
                    <Badge bg={config.variant} style={{ color: "black" }}>
                        {config.label}
                    </Badge>
                )
            }
        },
        {
            dataField: "extension",
            text: "Extension",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: (cell) => {
                const value = displayValue(cell);
                return (
                    <span
                        style={{
                            fontWeight: 600,
                            color: canEdit ? (isDark ? "rgb(96, 165, 250)" : "rgb(13, 110, 253)") : "inherit",
                        }}
                    >
                        {value}
                    </span>
                );
            },
        },
        {
            dataField: "effective_caller_id_name",
            text: "Effective CID Name",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
        {
            dataField: "outbound_caller_id_name",
            text: "Outbound Name",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
        {
            dataField: "outbound_caller_id_number",
            text: "Outbound Number",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
        {
            dataField: "call_group",
            text: "Call Group",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
        {
            dataField: "address",
            text: "Address",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
        {
            dataField: "device_template",
            text: "Template",
            sort: true,
            headerStyle: { cursor: "pointer" },
            formatter: displayValue,
        },
        {
            dataField: "user_context",
            text: "Context",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
        {
            dataField: "enabled",
            text: "Enabled",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: (val) => (toBoolean(val) ? "Yes" : "No"),
        },
        {
            dataField: "description",
            text: "Description",
            sort: true,
            headerStyle: {
                cursor: "pointer",
            },
            formatter: displayValue,
        },
    ];

    const searchableFields = React.useMemo(
        () => columns.map(col => col.dataField).filter(Boolean),
        [columns]
    );

    /* ------------------ DATA FETCH ------------------ */

    React.useEffect(() => {
        const loadSummary = async () => {
            try {
                const site = JSON.parse(localStorage.getItem("selectedSite"));
                const summary = await getExtensionsSummary(site?.site_uuid);
                setExtensionsSummary(summary);
            } catch (err) {
                console.error(err);
            }
        };

        loadSummary();
    }, []);

    const fetchRows = async () => {
        setLoading(true);
        try {
            const site = JSON.parse(localStorage.getItem("selectedSite"));
            const site_uuid = site?.site_uuid;

            const res = await fetch(
                `${serverHost}/get_extensions_full?domain_uuid=${site_uuid}`
            );

            if (!res.ok) throw new Error("Failed to fetch extensions");

            const data = await res.json();
            setRows(data.results || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const hasAvailableExts = async () => {

        try {
            const clientId = localStorage.getItem("clientId");
            console.log(clientId)
            const res = await fetch(
                `${serverHost}/has_avail_extensions/${clientId}`
            );

            if (!res.ok) throw new Error("Failed to fetch extensions");
            setCanCreate(res.can_create === "True")
        } catch (err) {
            console.error("Failed to check extension creation ability");
            
        }
    }

    useEffect(() => {
        hasAvailableExts()
        fetchRows();
    }, []);

    /* ------------------ DELETE ------------------ */

    const handleDelete = async (deleteVoicemailParam) => {
        try {
            if (selectedRows.length === 0) {
                throw new Error("No extensions selected for deletion");
            }

            for (const uuid of selectedRows) {
                const res = await fetch(`${serverHost}/delete_extension_full`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        extension_uuid: uuid,
                        delete_voicemail: deleteVoicemailParam,
                    }),
                });

                if (!res.ok) {
                    const msg = await res.text();
                    throw new Error(`Failed to delete extension ${uuid}: ${msg}`);
                }
            }

            setDeleteModal(false);
            setSelectedRows([]);
            fetchRows();
        } catch (err) {
            alert(err.message);
        }
    };

    /* ------------------ TOGGLE ENABLED ------------------ */

    const handleToggleEnabled = async () => {
        if (selectedRows.length === 0) return;
        setSelectedRows([]);

        // Close the modal immediately
        setToggleModal(false);

        try {
            await toggleExtensions(selectedRows);

            // Refresh table and summary
            await fetchRows();
            const site = JSON.parse(localStorage.getItem("selectedSite"));
            const summary = await getExtensionsSummary(site?.site_uuid);
            setExtensionsSummary(summary);

        } catch (err) {
            alert(err.message);
        }
    };

    /* ------------------ FILTER ------------------ */

    const selectedExtensionRows = React.useMemo(() => {
        const selectedSet = new Set(selectedRows);
        return rows.filter(r => selectedSet.has(r.extension_uuid));
    }, [rows, selectedRows]);

    const selectedExtensionNumbers = React.useMemo(() => {
        return selectedExtensionRows
            .map(r => r.extension)
            .filter(v => v !== null && v !== undefined && v !== "");
    }, [selectedExtensionRows]);

    const filteredRows = rows.filter((row) =>
        searchableFields.some((key) => {
            let value = row[key];

            // Normalize booleans to match what's displayed
            if (key === "enabled" || key === "call_screen_enabled") {
                value = toBoolean(value) ? "yes" : "no";
            }

            return String(value ?? "")
                .toLowerCase()
                .includes(search.toLowerCase());
        })
    );

    /* ------------------ STATES ------------------ */

    if (loading)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" variant={isDark ? "light" : "primary"} />
            </div>
        );

    if (error)
        return (
            <Alert variant="danger" className="mt-5 text-center">
                {error}
            </Alert>
        );

    return (
        <>
            <HusariaHeader name={title} parentName={parentName} padding="9rem" />

            <Container fluid className="mt--8">
                {/* ===================== STATISTICS CARDS ===================== */}
                <Row className="mb-4">
                    <Col lg="4" md="4" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p
                                            className="mb-0"
                                            style={{
                                                fontSize: "0.875rem",
                                                color: isDark ? "#adb5bd" : "#6c757d",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Number of Extensions
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700 }}>
                                            {extensionsSummary.extensions ?? 0}
                                        </h2>
                                    </div>
                                    <i className="fas fa-mobile-alt" style={{ fontSize: "2.5rem" }} />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg="4" md="4" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p
                                            className="mb-0"
                                            style={{
                                                fontSize: "0.875rem",
                                                color: isDark ? "#adb5bd" : "#6c757d",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Enabled Extensions
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700, color: "#28a745" }}>
                                            {extensionsSummary.extensions - (extensionsSummary.disabled ?? 0)}
                                        </h2>
                                    </div>

                                    <i
                                        className="fas fa-phone text-success"
                                        style={{ fontSize: "2.5rem" }}
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg="4" md="4" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p
                                            className="mb-0"
                                            style={{
                                                fontSize: "0.875rem",
                                                color: isDark ? "#adb5bd" : "#6c757d",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Disabled Extensions
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700, color: "#c61b1b" }}>
                                            {extensionsSummary.disabled ?? 0}
                                        </h2>
                                    </div>

                                    <i
                                        className={`fas fa-phone-slash ${(extensionsSummary.disabled ?? 0) === 0
                                            ? "text-success"
                                            : "text-warning"
                                            }`}
                                        style={{ fontSize: "2.5rem" }}
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* ===================== EXTENSIONS TABLE ===================== */}
                <Row>
                    <Col>
                        <Card className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}>
                            {/* ===================== HEADER ===================== */}
                            <Card.Header
                                className="d-flex justify-content-between align-items-center"
                                style={{
                                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                                    color: isDark ? "#e2e8f0" : "#111",
                                }}
                            >
                                {/* LEFT SIDE: Title + Search */}
                                <div className="d-flex align-items-center">
                                    <Form className="navbar-search navbar-search-light form-inline">
                                        <FormGroup className="mb-0">
                                            <InputGroup className="input-group-alternative input-group-merge search-border">
                                                <InputGroupText>
                                                    <i
                                                        className="fas fa-search search-icon"
                                                        style={{ color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)" }}
                                                    />
                                                </InputGroupText>
                                                <Input
                                                    className="form-control table-search-bar"
                                                    placeholder="Search"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Form>
                                </div>

                                {/* RIGHT: Actions */}
                                <div className="d-flex gap-2">

                                    {canEdit && selectedRows.length > 0 && (
                                        <Button
                                            size="sm"
                                            variant="info"
                                            onClick={() => setToggleModal(true)}
                                        >
                                            Toggle Enabled ({selectedRows.length})
                                        </Button>
                                    )}

                                    {canDelete && selectedRows.length > 0 && (
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            onClick={() => {
                                                setDeleteVoicemail(true);
                                                setDeleteModal(true);
                                            }}
                                        >
                                            Delete Selected ({selectedRows.length})
                                        </Button>
                                    )}

                                    {hasPermission(userData, PERMISSIONS.CREATE) && (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() => navigate("/admin/hucs/add/extension")}
                                            disabled={!canCreate}
                                        >
                                            + Add Extension
                                        </Button>
                                    )}
                                </div>
                            </Card.Header>

                            {/* ===================== TABLE BODY ===================== */}
                            <Card.Body className="p-0">
                                <div className="table-responsive py-4">
                                    <BootstrapTable
                                        keyField="extension_uuid"
                                        data={filteredRows}
                                        columns={columns}
                                        selectRow={canDelete ? selectRow : undefined}
                                        bootstrap4
                                        bordered={false}
                                        hover
                                        pagination={pagination}
                                        rowStyle={{ cursor: canEdit ? "pointer" : "default" }}
                                        rowEvents={
                                            canEdit
                                                ? {
                                                    onClick: (e, row) => {
                                                        navigate(
                                                            `/admin/hucs/edit/extension/${row.extension_uuid}`
                                                        );
                                                    },
                                                }
                                                : {}
                                        }
                                        classes="align-items-center table-flush"
                                        noDataIndication={() =>
                                            loading ? (
                                                <div className="text-center">Loading...</div>
                                            ) : (
                                                <div className="text-center">No extensions found.</div>
                                            )
                                        }
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* ===================== TOGGLE ENABLED MODAL ===================== */}
                <Modal show={toggleModal} onHide={() => setToggleModal(false)}>
                    <Modal.Header
                        closeButton
                        style={{ backgroundColor: isDark ? "#1f2937" : "#fff" }}
                    >
                        <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                            {selectedRows.length === 1 ? "Toggle Extension" : "Toggle Extensions"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body
                        style={{
                            backgroundColor: isDark ? "#1f2937" : "#fff",
                            color: isDark ? "#e2e8f0" : "#212529",
                        }}
                    >
                        Are you sure you want to toggle{" "}
                        <strong>
                            {selectedRows.length === 1 ? "this extension" : `${selectedRows.length} extensions`}
                        </strong>?
                    </Modal.Body>

                    <Modal.Footer
                        style={{ backgroundColor: isDark ? "#111827" : "#f8f9fa" }}
                    >
                        <Button variant="light" onClick={() => setToggleModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleToggleEnabled}>
                            Toggle Enabled
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ===================== DELETE MODAL ===================== */}
                <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
                    <Modal.Header
                        closeButton
                        style={{ backgroundColor: isDark ? "#1f2937" : "#fff" }}
                    >
                        <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                            {selectedRows.length === 1 ? (
                                <>
                                    Delete Extension{" "}
                                    <span className="text-danger fw-semibold">
                                        {selectedExtensionNumbers[0] ?? ""}
                                    </span>
                                </>
                            ) : (
                                <>Delete Extensions</>
                            )}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body
                        style={{
                            backgroundColor: isDark ? "#1f2937" : "#fff",
                            color: isDark ? "#e2e8f0" : "#212529",
                        }}
                    >
                        {selectedRows.length === 1 ? (
                            <>
                                Are you sure you want to delete this extension?
                                <br />
                                <strong>This action cannot be undone.</strong>
                            </>
                        ) : (
                            <>
                                Are you sure you want to delete these <strong>{selectedRows.length}</strong> extensions?
                                <br />
                                <strong>This action cannot be undone.</strong>

                                {selectedExtensionNumbers.length > 0 && (
                                    <div className="mt-3">
                                        <div className="fw-semibold mb-1">Selected:</div>
                                        <div style={{ opacity: isDark ? 0.9 : 0.85 }}>
                                            {selectedExtensionNumbers.slice(0, 8).join(", ")}
                                            {selectedExtensionNumbers.length > 8 ? `, +${selectedExtensionNumbers.length - 8} more` : ""}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </Modal.Body>

                    <Modal.Footer
                        className="d-flex justify-content-between align-items-center"
                        style={{ backgroundColor: isDark ? "#111827" : "#f8f9fa" }}
                    >
                        <div className="d-flex gap-2 w-100 justify-content-end">
                            <Button variant="light" onClick={() => setDeleteModal(false)}>
                                Cancel
                            </Button>

                            <Button variant="danger" onClick={() => handleDelete(false)}>
                                <i className="fas fa-phone mr-2"></i>
                                Extension Only
                            </Button>

                            <Button variant="danger" onClick={() => handleDelete(true)}>
                                <i className="fas fa-voicemail mr-2"></i>
                                Extension & Voicemail
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>
            </Container>
        </>
    );
}

export default ExtensionsTable;
